<template>
  <Popup class="username-editor-popup" ref="popup" @close="$emit('close')">
    <article class="username-editor">
      <LoadingOverlay v-if="isSaving" />

      <h2>{{ $t('adaptive-test-battle.username-editor-popup-heading') }}</h2>

      <PublicUsernameEditor
        ref="editor"
        :word-one-data="wordOneData"
        :word-two-data="wordTwoData"
      />
      <ErrorMessage v-if="hasError">
        <p>{{ errorMessage }}</p>
      </ErrorMessage>

      <div class="buttons">
        <ThreeDButton @click="$refs.editor.populatedAndShuffleWordOptions()">
          {{ $t('adaptive-test-battle.reload-words-button') }}
          <FaIcon class="redo-icon" icon="redo" />
        </ThreeDButton>

        <ConfirmChoiceButton @click="saveChanges" />
      </div>
    </article>
  </Popup>
</template>

<script>
import Popup from '../../ui/Popup'
import FaIcon from '../../ui/FaIcon'
import PublicUsernameEditor from './PublicUsernameEditor'
import LoadingOverlay from '../../LoadingOverlay'

import UserApi from '../../../apis/user'
import ErrorMessage from '../../ui/ErrorMessage'
import ConfirmChoiceButton from './ConfirmChoiceButton'
import ThreeDButton from '../../ThreeDButton'

export default {
  name: 'PublicUsernameEditorPopup',
  components: {
    ThreeDButton,
    ConfirmChoiceButton,
    ErrorMessage,
    LoadingOverlay,
    PublicUsernameEditor,
    FaIcon,
    Popup,
  },
  props: {
    show: Boolean,
    wordOneData: Object,
    wordTwoData: Object,
  },
  data() {
    return {
      isSaving: false,
      hasError: false,
      errorMessage: null,
    }
  },
  methods: {
    saveChanges() {
      let editedWords = this.$refs.editor.getEditedWords()

      if (
        this.wordOneData['word'] !== editedWords['wordOne']['word'] ||
        this.wordTwoData['word'] !== editedWords['wordTwo']['word']
      ) {
        this.hasError = false
        this.errorMessage = null
        this.isSaving = true

        new UserApi()
          .setPublicUsername(
            editedWords['wordOne']['id'],
            editedWords['wordTwo']['id']
          )
          .then(() => {
            this.isSaving = false
            this.$emit(
              'usernameSaved',
              editedWords['wordOne'],
              editedWords['wordTwo']
            )
          })
          .catch(({ message }) => {
            this.isSaving = false
            this.hasError = true
            this.errorMessage = message
          })
      } else {
        this.$emit('close')
      }
    },
  },
  watch: {
    show(show) {
      this.$refs.popup.togglePopup(show)
    },
  },
}
</script>

<style lang="scss" scoped>
::v-deep .popup {
  $stripeLight: $brighterYellowPale;
  $stripeDark: $orangeVeryPale;
  $baseWidth: 1.5em;

  border-radius: $mainBorderRadius;
  background: repeating-linear-gradient(
    90deg,
    $stripeDark,
    $stripeDark $baseWidth,
    $stripeLight $baseWidth,
    $stripeLight ($baseWidth * 2)
  );

  @media (min-width: $popupBreakpoint) and (min-height: 700px) {
    max-height: 80vh;
  }

  .username-editor {
    display: flex;
    flex-flow: column;
    align-items: center;
    width: 100%;
    flex: 1;
    font-size: 1.5em;

    > h2 {
      margin-bottom: 1em;
    }

    .buttons {
      margin-top: -2em;

      > .confirm-choice-button {
        margin-left: 1em;
        width: 3.3em;
        height: 3.3em;
      }

      .redo-icon {
        margin-left: 0.5em;
      }
    }
  }

  ::v-deep .public-username-editor {
    min-width: 65%;
    max-width: 100%;
    border: 0.2em solid $violet;
    border-radius: $mainBorderRadius;

    @media screen and (max-width: $popupBreakpoint) {
      .word-options {
        grid-template-columns: repeat(2, 1fr);

        .word-option:last-of-type {
          display: none;
        }
      }
    }
  }
}

.username-editor-popup {
  ::v-deep .popup {
    width: 100%;
    max-width: $minWidthDesktop;
  }
}

.reload-words-button {
  margin-left: 0.5em;
}
</style>
